import { contains, equals, FilterExpression } from '@infuse/api/common';

import { getChargingStation, removeFromLocation, searchChargingStations } from '@infuse/api/cpms/operator';
import { ChargingStationType, useChargingStationTypeStore } from '@infuse/cpms/config/store';
import { ColumnSort, Order } from '@infuse/ui/domain';
import { defineStore } from 'pinia';

import { ActionStatus, backendFilterForActionStatus, FriendlyStatus } from './actionStatus';
import { apiResultToDomain, enrichWithInstallerAliases } from './conversion';
import { aggregateSearchQueryAndFilters, State } from './filtering';
import { ChargingStation } from './model';
import { getDefaultSortParams } from './sortParams';
import { createStationGrouper, type StationGroup } from './stationGroups';

export const useChargingStationStore = defineStore('charging-station', {
  state: () => {
    const countsPerActionState = Object.fromEntries(Object.values(ActionStatus).map((k) => [k, 0]));
    const countsPerStatusState = Object.fromEntries(Object.values(FriendlyStatus).map((k) => [k, 0]));
    const defaultSortParams = getDefaultSortParams();

    return {
      items: [],
      item: undefined,
      options: {
        query: '',
        filter: undefined,
        page: 0,
        lastPage: false,
        distinct: false,
        pageSize: 20,
        confItems: [],
        defaultSort: [defaultSortParams],
        rootId: undefined,
        domainOnly: undefined,
        currentSearchId: undefined,
        includeTotal: false,
      },
      loading: false,
      counts: { ...countsPerActionState, total: 0 },
      statusCounts: { ...countsPerStatusState, total: 0 },
      totalCount: 0,
      matchingTotal: 0,
      error: false
    } as State;
  },
  getters: {
    getChargingStationsByFriendlyStatus: (state: State) => {
      return (status: FriendlyStatus) =>
        state.items.filter((chargingStation) => chargingStation.friendlyStatus === status);
    },
    getChargingStationsByActionStatus: (state: State) => {
      return (status: ActionStatus) => state.items.filter((chargingStation) => chargingStation.actionStatus === status);
    }
  },
  actions: {
    async search() {
      const searchId = Date.now();
      try {
        this.loading = true;
        this.error = false;

        // Generate a unique identifier for this search request
        this.options.currentSearchId = searchId;

        const apiResponse = await searchChargingStations({
          sort: !this.options.sort?.length ? this.options.defaultSort : this.options.sort,
          size: this.options.pageSize,
          filter: aggregateSearchQueryAndFilters(this.options),
          page: this.options.page,
          rootId: this.options.rootId,
          domainOnly: this.options.domainOnly,
          // attribute filters (vendor/model, firmware) join the attributes table per condition; distinct prevents duplicate rows
          distinct: true,
          // opt-in: only the surfaces that offer "select all N matching" pay for the count
          includeTotal: this.options.includeTotal ?? false
        });

        // Ignore results if a newer search has been initiated
        if (this.options.currentSearchId !== searchId) {
          return;
        }

        this.options.lastPage = apiResponse.last;
        // A page fetched without includeTotal must not clobber the known total.
        if (this.options.includeTotal && apiResponse.totalElements != null) {
          this.matchingTotal = apiResponse.totalElements;
        }

        let responseItems = apiResponse.content ? apiResponse.content.map(apiResultToDomain) : [];

        if (this.options.replaceInstallerAlias) {
          responseItems = await enrichWithInstallerAliases(responseItems, this.options);

          // Check again after async operation
          if (this.options.currentSearchId !== searchId) {
            return;
          }
        }

        const chargingStationTypes = useChargingStationTypeStore().items;
        if (chargingStationTypes.length) {
          responseItems = await Promise.all(
            responseItems.map((cs) => this.enrichChargingStation(cs, chargingStationTypes))
          );
          if (this.options.currentSearchId !== searchId) {
            return;
          }
        }

        if (apiResponse.first) {
          this.items = responseItems;
        } else {
          this.items = [...this.items, ...responseItems];
        }
      } catch (error) {
        if (this.options.currentSearchId === searchId) {
          this.error = true;
          throw error;
        }
      } finally {
        if (this.options.currentSearchId === searchId) {
          this.loading = false;
        }
      }
    },

    async setSearchQuery(searchQuery: string) {
      this.resetItems();
      this.options.query = searchQuery;

      await this.search();
    },

    async setFilter(filter: FilterExpression) {
      this.resetItems();
      this.options.filter = filter;

      await this.search();
    },

    async loadMore() {
      if (!this.options.lastPage) {
        this.options.page++;
        await this.search();
      }
    },

    async enrichChargingStation(
      chargingStation: ChargingStation,
      chargingStationTypes: ChargingStationType[]
    ): Promise<ChargingStation> {
      if (chargingStation.type?.vendor && chargingStation.type?.model) {
        const chargingStationType = chargingStationTypes.find(
          (type) => type.manufacturer.name === chargingStation.type?.vendor && type.type === chargingStation.type?.model
        );

        if (chargingStationType?.commercialType) {
          chargingStation.type.commercialModelName = chargingStationType.commercialType;
        }
      }

      return chargingStation;
    },

    async setSort(columnSort: ColumnSort) {
      if (!this.options.sort || this.options.resetSort) {
        this.options.sort = [];
      } else {
        this.options.sort = this.options.sort.filter((s) => s.key !== columnSort.columnName); // remove current sort for this column
      }

      switch (columnSort.order) {
        case Order.NONE:
          break;
        case Order.DESC:
          this.options.sort.push({ key: columnSort.columnName, descending: true });
          break;
        case Order.ASC:
          this.options.sort.push({ key: columnSort.columnName, descending: false });
          break;
      }

      this.resetItems();
      await this.search();
    },

    resetOptions() {
      this.options.query = '';
      this.options.filter = undefined;
      this.options.rootId = undefined;
      this.options.domainOnly = undefined;
      this.options.includeTotal = false;
      this.resetItems();
    },

    resetItems() {
      this.options.page = 0;
      this.options.lastPage = false;
      this.items = [];
      this.item = undefined;
      // Clear the matching total too, so an uncounted search after a counted one can't
      // expose a stale count (matchingTotal is only re-set when includeTotal is on).
      this.matchingTotal = 0;
    },

    // this is a waste performance-wise: we load the charging stations for the
    // action statuses just to know how many there are.
    // let's hope we can solve this by building a metrics/statistics/dashboarding
    // module in CPMS Core that can serve these numbers from a single row in a
    // single request before the end of 2022.
    async loadActionStatusCounts() {
      for (const actionStatus of Object.values(ActionStatus)) {
        const apiResponse = await searchChargingStations({
          size: 1,
          filter: backendFilterForActionStatus(actionStatus)
        });

        this.counts[actionStatus] = apiResponse.totalElements || 0;
      }

      const apiResponse = await searchChargingStations({
        size: 1
      });

      this.counts.total = apiResponse.totalElements || 0;
    },

    async getChargingStation(id: string): Promise<ChargingStation> {
      this.loading = true;
      try {
        this.item = apiResultToDomain(await getChargingStation({ id }));
        return this.item;
      } finally {
        // Always clear loading, even when the request rejects (e.g. a 403/404
        // is expected when probing access during the claim flow). Otherwise the
        // store stays stuck in a loading state.
        this.loading = false;
      }
    },

    async setPoolFilter(poolId: string, { includeTotal = false } = {}): Promise<void> {
      this.resetOptions();
      this.options.filter = equals('locationId', poolId);
      this.options.includeTotal = includeTotal;

      await this.search();
    },

    /**
     * Scope the list to one location. `includeTotal` opts into the filtered total
     * (needed for "select all N matching"); it must be set here because this action
     * resets the options before searching.
     */
    async setLocationFilter(locationId: string, { includeTotal = false } = {}): Promise<void> {
      this.resetOptions();
      this.options.filter = contains('pools.id', [locationId], false);
      this.options.includeTotal = includeTotal;

      await this.search();
    },

    async setBatchPoolFilter(stationFilter: {
      locationId: string;
      vendor: string;
      model: string;
      currentFirmware: string;
    }): Promise<void> {
      this.resetOptions();

      const filter = new FilterExpression();

      if (stationFilter.locationId && stationFilter.locationId !== 'all') {
        filter.and(equals('locationId', stationFilter.locationId));
      }

      if (stationFilter.vendor && stationFilter.vendor !== 'Undefined') {
        filter.and(equals('attributes.key', 'vendorId').and(equals('attributes.value', stationFilter.vendor)));
      }

      if (stationFilter.model && stationFilter.model !== 'Undefined') {
        filter.and(equals('attributes.key', 'model').and(equals('attributes.value', stationFilter.model)));
      }

      if (stationFilter.currentFirmware && stationFilter.currentFirmware !== 'Undefined') {
        filter.and(
          equals('attributes.key', 'firmwareVersion').and(equals('attributes.value', stationFilter.currentFirmware))
        );
      }

      filter.and(equals('accepted', true));

      try {
        this.loading = true;

        const apiResponse = await searchChargingStations({
          sort: this.options.sort ?? [{ key: 'id' }],
          filter: filter,
          page: 0,
          distinct: true
        });

        this.totalCount = apiResponse.totalElements;
        this.options.lastPage = apiResponse.last;

        const responseItems = apiResponse.content ? apiResponse.content.map(apiResultToDomain) : [];

        if (apiResponse.first) {
          this.items = responseItems;
        } else {
          this.items = [...this.items, ...responseItems];
        }
      } finally {
        this.loading = false;
      }
    },
    async getAllBatchPoolStations(stationFilter: {
      locationId: string;
      vendor: string;
      model: string;
      currentFirmware: string;
    }): Promise<ChargingStation[]> {
      const filter = new FilterExpression();

      if (stationFilter.locationId && stationFilter.locationId !== 'all') {
        filter.and(equals('locationId', stationFilter.locationId));
      }

      if (stationFilter.vendor && stationFilter.vendor !== 'Undefined') {
        filter.and(equals('attributes.key', 'vendorId').and(equals('attributes.value', stationFilter.vendor)));
      }

      if (stationFilter.model && stationFilter.model !== 'Undefined') {
        filter.and(equals('attributes.key', 'model').and(equals('attributes.value', stationFilter.model)));
      }

      if (stationFilter.currentFirmware && stationFilter.currentFirmware !== 'Undefined') {
        filter.and(
          equals('attributes.key', 'firmwareVersion').and(equals('attributes.value', stationFilter.currentFirmware))
        );
      }

      filter.and(equals('accepted', true));

      const allStations: ChargingStation[] = [];
      let page = 0;
      let lastPage = false;

      while (!lastPage) {
        const response = await searchChargingStations({
          sort: [{ key: 'id' }],
          page,
          filter,
          distinct: true,
          includeTotal: false
        });

        const stations = response.content?.map(apiResultToDomain) ?? [];
        allStations.push(...stations);

        lastPage = response.last;
        page++;
      }

      return allStations;
    },

    async fetchAllMatchingIds(
      excluding: ReadonlySet<string> = new Set()
    ): Promise<{ ids: string[]; groups: StationGroup[] }> {
      const filter = aggregateSearchQueryAndFilters(this.options);
      const pageSize = 500;
      const maxPages = 200;
      // Tiebreak on id so rows cannot shift between pages.
      const sort = [...(this.options.sort ?? []), { key: 'id' }];
      const ids: string[] = [];
      const grouper = createStationGrouper();
      let page = 0;
      let lastPage = false;

      while (!lastPage && page < maxPages) {
        const response = await searchChargingStations({
          sort,
          size: pageSize,
          filter,
          page,
          rootId: this.options.rootId,
          domainOnly: this.options.domainOnly,
          distinct: true,
          includeTotal: false
        });

        const pageStations = response.content ?? [];
        let stations = pageStations.map(apiResultToDomain);
        if (this.options.replaceInstallerAlias) {
          stations = await enrichWithInstallerAliases(stations, this.options);
        }
        stations
          .filter((station) => !excluding.has(station.id))
          .forEach((station) => {
            ids.push(station.id);
            grouper.add(station);
          });
        lastPage = response.last;
        if (pageStations.length === 0) {
          break;
        }
        page++;
      }

      // A partial set would run the batch against the wrong stations.
      if (!lastPage && page >= maxPages) {
        throw new Error(`fetchAllMatchingIds exceeded ${maxPages} pages; matching set too large to enumerate`);
      }

      return { ids, groups: grouper.groups() };
    },

    async setIdSetFilter(chargingStationIds: string[]): Promise<void> {
      this.resetOptions();
      this.options.filter = contains('id', chargingStationIds);

      await this.search();
    },

    async removeChargingStationFromPool(chargingStationId: string, refreshItems = true) {
      await removeFromLocation({ chargingStationId: chargingStationId });

      if (refreshItems) {
        await this.refresh();
      }
    },

    async refresh() {
      this.resetItems();
      await this.search();
    }
  }
});
